/* eslint-disable max-len */
import { NavLink } from 'react-router';

import { UikitMenuSidebar } from '../../components/UikitMenuSidebar';
import { UikitMenu } from '../../components/UikitMenu';
import { NewBadge } from '../components/ComponentsMenu';

import { Alerts } from './alerts/Alerts';
import { Animations } from './animations/Animations';
import { AspectRatio } from './aspectRatio/AspectRatio';
import { Background } from './background/Background';
import { BackgroundColors } from './backgroundColors/BackgroundColors';
import { Badges } from './badges/Badges';
import { Blockquote } from './blockquote/Blockquote';
import { Blur } from './blur/Blur';
import { Border } from './border/Border';
import { Breadcrumb } from './breadcrumb/Breadcrumb';
import { Buttons } from './buttons/Buttons';
import { Callouts } from './callouts/Callouts';
import { Code } from './code/Code';
import { Cols } from './cols/Cols';
import { Columns } from './columns/Columns';
import { Container } from './container/Container';
import { ContainerQueries } from './containerQueries/ContainerQueries';
import { Cursor } from './cursor/Cursor';
import { Divider } from './divider/Divider';
import { Details } from './details/Details';
import { Display } from './display/Display';
import { Ellipsis } from './ellipsis/Ellipsis';
import { FieldSizing } from './fieldSizing/FieldSizing';
import { Flexbox } from './flexbox/Flexbox';
import { Forms } from './forms/Forms';
import { Gap } from './gap/Gap';
import { Counters } from './counter/Counter';
import { Grid } from './grid/Grid';
import { Subgrid } from './grid/Subgrid';
import { Height } from './height/Height';
import { Helper } from './helper/Helper';
import { Inset } from './inset/Inset';
import { Key } from './key/Key';
import { Labels } from './labels/Labels';
import { LineHeight } from './lineHeight/LineHeight';
import { ListGroups } from './listGroups/ListGroups';
import { Lists } from './lists/Lists';
import { Margin } from './margin/Margin';
import { Math } from './math/Math';
import { Opacity } from './opacity/Opacity';
import { Overflow } from './overflow/Overflow';
import { Padding } from './padding/Padding';
import { Pager } from './pager/Pager';
import { Pagination } from './pagination/Pagination';
import { Panels } from './panels/Panels';
import { Pills } from './pills/Pills';
import { Position } from './position/Position';
import { ProgressBars } from './progressBars/ProgressBars';
import { Icon } from './icon/Icon';
import { IconVehicle } from './icon/IconVehicle';
import { IconPair } from './icon/IconPair';
import { IconAnimated } from './icon/IconAnimated';
import { IconFilled } from './icon/IconFilled';
import { IconCustom } from './icon/IconCustom';
import { IconTextAlignment } from './icon/IconTextAlignment';
import { IconLibrary } from './icon/IconLibrary';
import { IconDisabled } from './icon/IconDisabled';
import { Rotate } from './rotate/Rotate';
import { Rounded } from './rounded/Rounded';
import { Scale } from './scale/Scale';
import { Shadows } from './shadows/Shadows';
import { Space } from './space/Space';
import { SvgColors } from './svgColors/SvgColors';
import { Tabs } from './tabs/Tabs';
import { TextAlignment } from './textAlignment/TextAlignment';
import { TextColors } from './textColors/TextColors';
import { TextStyles } from './textStyles/TextStyles';
import { TextBox } from './textBox/TextBox';
import { TextWrapping } from './textWrapping/TextWrapping';
import { Transition } from './transition/Transition';
import { Translate } from './translate/Translate';
import { UserSelection } from './userSelection/UserSelection';
import { Visibility } from './visibility/Visibility';
import { Wells } from './wells/Wells';
import { Width } from './width/Width';
import { Zindex } from './zIndex/Zindex';

import { routes } from '../../routes';

type MenuItem = {
    key: string;
    item: JSX.Element;
    page: JSX.Element;
    meta: string;
    isSubItem?: boolean;
};

type MenuGroup = {
    group: string;
    navItems: MenuItem[];
    badge?: string;
};

export const BASE_URL: string = `/${routes.FOUNDATIONS}`;
const title = 'Foundations';

const navLinkClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'active' : undefined);

export const getFoundationsMenuItems = (): MenuGroup[] => {
    const menu: MenuGroup[] = [
        {
            group: 'Interaction',
            navItems: [
                {
                    key: 'buttons',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/buttons`}>
                            Buttons
                        </NavLink>
                    ),
                    page: <Buttons />,
                    meta: 'buttons',
                },
                {
                    key: 'details',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/details`}>
                            Details
                            <NewBadge />
                        </NavLink>
                    ),
                    page: <Details />,
                    meta: 'details summary disclosure expand collapse animation',
                },
            ],
        },

        {
            group: 'Colors',
            navItems: [
                {
                    key: 'backgroundColors',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/backgroundColors`}>
                            Background
                        </NavLink>
                    ),
                    page: <BackgroundColors />,
                    meta: 'backgroundColors bg',
                },
                {
                    key: 'textColors',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/textColors`}>
                            Text
                        </NavLink>
                    ),
                    page: <TextColors />,
                    meta: 'textColors font fontcolor',
                },
                {
                    key: 'svgColors',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/svgColors`}>
                            SVG
                        </NavLink>
                    ),
                    page: <SvgColors />,
                    meta: 'svgColors fill',
                },
            ],
        },

        {
            group: 'Styling',
            navItems: [
                {
                    key: 'background',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/background`}>
                            Background
                        </NavLink>
                    ),
                    page: <Background />,
                    meta: 'background bg',
                },
                {
                    key: 'border',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/border`}>
                            Border
                        </NavLink>
                    ),
                    page: <Border />,
                    meta: 'border',
                },
                {
                    key: 'divider',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/divider`}>
                            Divider
                        </NavLink>
                    ),
                    page: <Divider />,
                    meta: 'divider separator',
                },
                {
                    key: 'opacity',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/opacity`}>
                            Opacity
                        </NavLink>
                    ),
                    page: <Opacity />,
                    meta: 'opacity transparency',
                },
                {
                    key: 'rounded',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/rounded`}>
                            Rounded
                        </NavLink>
                    ),
                    page: <Rounded />,
                    meta: 'rounded border radius',
                },
                {
                    key: 'shadows',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/shadows`}>
                            Shadows
                        </NavLink>
                    ),
                    page: <Shadows />,
                    meta: 'shadows box-shadow',
                },
            ],
        },

        {
            group: 'Icons',
            navItems: [
                {
                    key: 'icons',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/icons`}>
                            Icons
                        </NavLink>
                    ),
                    page: <Icon />,
                    meta: 'uikit-icon icons',
                },
                {
                    key: 'vehicleIcons',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/vehicleIcons`}>
                            Vehicle icons
                        </NavLink>
                    ),
                    page: <IconVehicle />,
                    meta: 'uikit-icon icons vehicle',
                },
                {
                    key: 'pairIcons',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/pairIcons`}>
                            Pair icons
                        </NavLink>
                    ),
                    page: <IconPair />,
                    meta: 'uikit-icon icons pair',
                },
                {
                    key: 'animatedIcons',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/animatedIcons`}>
                            Animated icons
                        </NavLink>
                    ),
                    page: <IconAnimated />,
                    meta: 'uikit-icon icons animated',
                },
                {
                    key: 'disabledIcons',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/disabledIcons`}>
                            Disabled icons
                        </NavLink>
                    ),
                    page: <IconDisabled />,
                    meta: 'uikit-icon icons disabled',
                },
                {
                    key: 'filledIcons',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/filledIcons`}>
                            Filled icons
                        </NavLink>
                    ),
                    page: <IconFilled />,
                    meta: 'uikit-icon icons filled',
                },
                {
                    key: 'customIcons',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/customIcons`}>
                            Custom icons
                        </NavLink>
                    ),
                    page: <IconCustom />,
                    meta: 'uikit-icon icons custom',
                },
                {
                    key: 'iconAlignment',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/iconAlignment`}>
                            UIKIT icon text alignment
                        </NavLink>
                    ),
                    page: <IconTextAlignment />,
                    meta: 'UIKIT icon text alignment',
                },
                {
                    key: 'iconLibrary',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/iconLibrary`}>
                            Icon library
                        </NavLink>
                    ),
                    page: <IconLibrary />,
                    meta: 'uikit-icon all iconlist cheatsheet icons iconLibrary',
                },
            ],
        },

        {
            group: 'Text',
            navItems: [
                {
                    key: 'textStyles',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/textStyles`}>
                            Text styles
                        </NavLink>
                    ),
                    page: <TextStyles />,
                    meta: 'textStyles font headline h1 h2 h3 h4 h5',
                },
                {
                    key: 'alignment',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/alignment`}>
                            Alignment
                        </NavLink>
                    ),
                    page: <TextAlignment />,
                    meta: 'alignment',
                },
                {
                    key: 'blockquote',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/blockquote`}>
                            Blockquote
                        </NavLink>
                    ),
                    page: <Blockquote />,
                    meta: 'blockquote',
                },
                {
                    key: 'code',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/code`}>
                            Code
                        </NavLink>
                    ),
                    page: <Code />,
                    meta: 'code pre',
                },
                {
                    key: 'math',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/math`}>
                            Mathematics
                            <NewBadge />
                        </NavLink>
                    ),
                    page: <Math />,
                    meta: 'math mathematics mathml formula equation fuel consumption',
                },
                {
                    key: 'cursors',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/cursors`}>
                            Cursor
                        </NavLink>
                    ),
                    page: <Cursor />,
                    meta: 'cursors',
                },
                {
                    key: 'ellipsis',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/ellipsis`}>
                            Ellipsis
                        </NavLink>
                    ),
                    page: <Ellipsis />,
                    meta: 'ellipsis truncating',
                },
                {
                    key: 'lineHeight',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/lineHeight`}>
                            Line height
                        </NavLink>
                    ),
                    page: <LineHeight />,
                    meta: 'lineHeight',
                },
                {
                    key: 'userSelect',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/userSelect`}>
                            User select
                        </NavLink>
                    ),
                    page: <UserSelection />,
                    meta: 'userSelect',
                },
                {
                    key: 'textBox',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/textBox`}>
                            Text box
                        </NavLink>
                    ),
                    page: <TextBox />,
                    meta: 'textBox text-box text-box-trim',
                },
                {
                    key: 'textWrapping',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/textWrapping`}>
                            Wrapping
                        </NavLink>
                    ),
                    page: <TextWrapping />,
                    meta: 'textWrapping',
                },
            ],
        },

        {
            group: 'Layout',
            navItems: [
                {
                    key: 'cols',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/cols`}>
                            Cols
                        </NavLink>
                    ),
                    page: <Cols />,
                    meta: 'cols columns',
                },
                {
                    key: 'columns',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/columns`}>
                            Columns
                            <NewBadge />
                        </NavLink>
                    ),
                    page: <Columns />,
                    meta: 'columns multi column column-count column-width column-rule',
                },
                {
                    key: 'container',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/container`}>
                            Container
                        </NavLink>
                    ),
                    page: <Container />,
                    meta: 'container',
                },
                {
                    key: 'containerQueries',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/containerQueries`}>
                            Container queries
                        </NavLink>
                    ),
                    page: <ContainerQueries />,
                    meta: 'container queries responsive',
                },
                {
                    key: 'flexbox',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/flexbox`}>
                            Flexbox
                        </NavLink>
                    ),
                    page: <Flexbox />,
                    meta: 'display flexbox',
                },
                {
                    key: 'grid',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/grid`}>
                            Grid
                        </NavLink>
                    ),
                    page: <Grid />,
                    meta: 'display grid',
                },
                {
                    key: 'subgrid',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/subgrid`}>
                            Subgrid
                        </NavLink>
                    ),
                    page: <Subgrid />,
                    meta: 'display subgrid grid',
                },
                {
                    key: 'position',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/position`}>
                            Position
                        </NavLink>
                    ),
                    page: <Position />,
                    meta: 'position absolute top left right bottom',
                },
            ],
        },

        {
            group: 'Spacing',
            navItems: [
                {
                    key: 'gap',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/gap`}>
                            Gap
                        </NavLink>
                    ),
                    page: <Gap />,
                    meta: 'gap',
                },
                {
                    key: 'space',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/space`}>
                            Space
                        </NavLink>
                    ),
                    page: <Space />,
                    meta: 'space',
                },
                {
                    key: 'margin',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/margin`}>
                            Margin
                        </NavLink>
                    ),
                    page: <Margin />,
                    meta: 'margin',
                },
                {
                    key: 'padding',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/padding`}>
                            Padding
                        </NavLink>
                    ),
                    page: <Padding />,
                    meta: 'padding',
                },
            ],
        },

        {
            group: 'Sizing',
            navItems: [
                {
                    key: 'height',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/height`}>
                            Height
                        </NavLink>
                    ),
                    page: <Height />,
                    meta: 'height size',
                },
                {
                    key: 'width',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/width`}>
                            Width
                        </NavLink>
                    ),
                    page: <Width />,
                    meta: 'width size',
                },
            ],
        },

        {
            group: 'Navigation',
            navItems: [
                {
                    key: 'breadcrumbs',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/breadcrumbs`}>
                            Breadcrumbs
                        </NavLink>
                    ),
                    page: <Breadcrumb />,
                    meta: 'breadcrumbs navigation',
                },
                {
                    key: 'pager',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/pager`}>
                            Pager
                        </NavLink>
                    ),
                    page: <Pager />,
                    meta: 'pager navigation',
                },
                {
                    key: 'pagination',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/pagination`}>
                            Pagination
                        </NavLink>
                    ),
                    page: <Pagination />,
                    meta: 'pagination navigation',
                },
                {
                    key: 'pills',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/pills`}>
                            Pills
                        </NavLink>
                    ),
                    page: <Pills />,
                    meta: 'pills navigation nav-pills stacked',
                },
                {
                    key: 'tabs',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/tabs`}>
                            Tabs
                        </NavLink>
                    ),
                    page: <Tabs />,
                    meta: 'tabs navigation',
                },
            ],
        },

        {
            group: 'Elements',
            navItems: [
                {
                    key: 'alerts',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/alerts`}>
                            Alerts
                        </NavLink>
                    ),
                    page: <Alerts />,
                    meta: 'alerts',
                },
                {
                    key: 'badges',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/badges`}>
                            Badges
                        </NavLink>
                    ),
                    page: <Badges />,
                    meta: 'badges',
                },
                {
                    key: 'callouts',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/callouts`}>
                            Callouts
                        </NavLink>
                    ),
                    page: <Callouts />,
                    meta: 'callouts',
                },
                {
                    key: 'counter',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/counter`}>
                            Counter
                        </NavLink>
                    ),
                    page: <Counters />,
                    meta: 'counter',
                },
                {
                    key: 'key',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/key`}>
                            Key
                        </NavLink>
                    ),
                    page: <Key />,
                    meta: 'key',
                },
                {
                    key: 'labels',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/labels`}>
                            Labels
                        </NavLink>
                    ),
                    page: <Labels />,
                    meta: 'labels',
                },
                {
                    key: 'lists',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/lists`}>
                            Lists
                        </NavLink>
                    ),
                    page: <Lists />,
                    meta: 'lists iconlist treelist',
                },
                {
                    key: 'listGroups',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/listGroups`}>
                            List groups
                        </NavLink>
                    ),
                    page: <ListGroups />,
                    meta: 'listGroups',
                },
                {
                    key: 'panels',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/panels`}>
                            Panels
                        </NavLink>
                    ),
                    page: <Panels />,
                    meta: 'panels',
                },
                {
                    key: 'progress',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/progress`}>
                            Progress bars
                        </NavLink>
                    ),
                    page: <ProgressBars />,
                    meta: 'progress',
                },
                {
                    key: 'wells',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/wells`}>
                            Wells
                        </NavLink>
                    ),
                    page: <Wells />,
                    meta: 'wells',
                },
            ],
        },

        {
            group: 'Forms',
            navItems: [
                {
                    key: 'forms',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/forms`}>
                            Forms
                        </NavLink>
                    ),
                    page: <Forms />,
                    meta: 'forms input select password fieldset legend',
                },
                {
                    key: 'fieldSizing',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/fieldSizing`}>
                            Field sizing
                            <NewBadge />
                        </NavLink>
                    ),
                    page: <FieldSizing />,
                    meta: 'field sizing textarea auto grow min height max height content',
                },
            ],
        },

        {
            group: 'Animate',
            navItems: [
                {
                    key: 'css-animations',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/css-animations`}>
                            CSS Animations
                        </NavLink>
                    ),
                    page: <Animations />,
                    meta: 'css animations',
                },
                {
                    key: 'transition',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/transition`}>
                            Transition
                        </NavLink>
                    ),
                    page: <Transition />,
                    meta: 'transition',
                },
            ],
        },

        {
            group: 'CSS Filter',
            navItems: [
                {
                    key: 'blur',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/blur`}>
                            Blur
                        </NavLink>
                    ),
                    page: <Blur />,
                    meta: 'blur',
                },
            ],
        },

        {
            group: 'Utilities',
            navItems: [
                {
                    key: 'aspectRatio',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/aspectRatio`}>
                            Aspect ratio
                        </NavLink>
                    ),
                    page: <AspectRatio />,
                    meta: 'aspectRatio',
                },
                {
                    key: 'inset',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/inset`}>
                            Inset
                        </NavLink>
                    ),
                    page: <Inset />,
                    meta: 'inset',
                },
                {
                    key: 'helper',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/helper`}>
                            Helper
                        </NavLink>
                    ),
                    page: <Helper />,
                    meta: 'helper',
                },
            ],
        },

        {
            group: 'Transform',
            navItems: [
                {
                    key: 'rotate',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/rotate`}>
                            Rotate
                        </NavLink>
                    ),
                    page: <Rotate />,
                    meta: 'rotate',
                },
                {
                    key: 'scale',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/scale`}>
                            Scale
                        </NavLink>
                    ),
                    page: <Scale />,
                    meta: 'scale',
                },
                {
                    key: 'translate',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/translate`}>
                            Translate
                        </NavLink>
                    ),
                    page: <Translate />,
                    meta: 'translate',
                },
            ],
        },

        {
            group: 'Visibility',
            navItems: [
                {
                    key: 'display',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/display`}>
                            Display
                        </NavLink>
                    ),
                    page: <Display />,
                    meta: 'display',
                },
                {
                    key: 'overflow',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/overflow`}>
                            Overflow
                        </NavLink>
                    ),
                    page: <Overflow />,
                    meta: 'overflow',
                },
                {
                    key: 'visibility',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/visibility`}>
                            Visibility
                        </NavLink>
                    ),
                    page: <Visibility />,
                    meta: 'visibility',
                },
                {
                    key: 'zIndex',
                    item: (
                        <NavLink className={navLinkClass} to={`${BASE_URL}/zIndex`}>
                            Z-Index
                        </NavLink>
                    ),
                    page: <Zindex />,
                    meta: 'zIndex',
                },
            ],
        },
    ];

    return menu.map(item => ({ ...item, badge: title }));
};

export const FoundationsMenu = () => (
    <UikitMenuSidebar title={title}>
        <UikitMenu />
    </UikitMenuSidebar>
);
